// useCmsSlot — Wave C slot consumer hook.
//
// Resolution order for a slot body:
//   1. The worker-injected `<script id="__wwaave_cms__">` blob. On any marketing
//      page this carries EVERY page's bodies (`pages`), not just the loaded
//      one — so both the hard-loaded page and every client-side navigation out
//      of it render the admin's copy on first paint, with no request.
//   2. A client fetch of `/api/__public__/cms?page=<id>` for the slot's page.
//      Only reached when the SPA was entered somewhere the worker doesn't
//      inject (an app route like /dashboard, say) — `prefetchMarketingCms`
//      normally warms every page from one `page=all` call before that happens.
//   3. The registry default from `@wwaave/shared/cms`.
//
// Why the whole set rather than the loaded page: the worker only ever sees the
// pathname the browser hard-loaded, so a per-page blob left every onward
// destination painting its baked defaults until a fetch resolved. That swap is
// the "loads the original hero and text for a second then flicks over" flash
// Richie reported; an idle-time prefetch narrowed the window but still lost the
// race to a fast click. It also fixes the site-wide pages (footer, next_steps),
// which have no pathname of their own and therefore ALWAYS client-fetched.

import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import {
  getSlotDef,
  type ImageBody,
  type ImageListBody,
  PAGE_IDS,
  type PageId,
  type SlotBody,
  type SlotBodyByKind,
  type SlotKind,
} from '@wwaave/shared/cms';

const SCRIPT_ID = '__wwaave_cms__';

type InjectedBlob = {
  page: string;
  bodies: Record<string, SlotBody>;
  /** Every page's bodies, keyed by PageId. Absent on pre-rollout cached HTML. */
  pages?: Record<string, Record<string, SlotBody>>;
};

/**
 * Parse the worker-injected blob at module init. Cached for the life of the
 * SPA — re-mount of a component should not refetch. Lazily initialised so
 * SSR / non-browser callers (none today, but defensive) don't error.
 */
let injected: InjectedBlob | null | undefined;

function readInjected(): InjectedBlob | null {
  if (injected !== undefined) return injected;
  if (typeof document === 'undefined') {
    injected = null;
    return null;
  }
  const el = document.getElementById(SCRIPT_ID);
  if (el === null) {
    injected = null;
    return null;
  }
  try {
    const parsed = JSON.parse(el.textContent ?? '') as unknown;
    if (parsed !== null && typeof parsed === 'object') {
      injected = parsed as InjectedBlob;
      return injected;
    }
  } catch {
    // fallthrough
  }
  injected = null;
  return null;
}

type PageBodies = Record<string, SlotBody>;

/**
 * The injected bodies for `page`, or undefined when the injection can't speak
 * for it (no blob at all, or pre-rollout HTML that only carried the loaded
 * page). An empty map is a meaningful answer — "this page has no overrides" —
 * so it seeds the query and lets `ready` flip true with no request.
 */
function seededBodiesFor(page: string): PageBodies | undefined {
  const blob = readInjected();
  if (blob === null) return undefined;
  if (blob.pages !== undefined) return blob.pages[page] ?? {};
  return blob.page === page ? blob.bodies : undefined;
}

async function fetchPageBodies(page: string): Promise<PageBodies> {
  const res = await fetch(`/api/__public__/cms?page=${encodeURIComponent(page)}`, {
    headers: { accept: 'application/json' },
  });
  if (!res.ok) return {};
  const json = (await res.json()) as { bodies?: PageBodies };
  return json.bodies ?? {};
}

/**
 * Lookup a slot body by key. Generic over the declared kind so callers get
 * the right shape (markdown → string, qa_list → QaItem[], etc.). Always
 * returns a valid body — falls back to the registry default while the page's
 * overrides are loading or when none exist.
 *
 * Internally this reads from a per-page TanStack Query keyed `['cms', page]`.
 * Multiple `useCmsSlot` calls on the same page share one fetch (react-query
 * dedupes by key), and the worker-injected blob seeds `initialData` so a
 * hard-load resolves synchronously with no request.
 */
export function useCmsSlot<K extends SlotKind>(key: string): SlotBodyByKind[K] {
  const def = getSlotDef(key);
  if (def === undefined) {
    throw new Error(`useCmsSlot: unknown key '${key}' — add it to packages/shared/src/cms/slots.ts`);
  }
  const page = def.page;

  const seeded = seededBodiesFor(page);

  const { data } = useQuery({
    queryKey: ['cms', page],
    queryFn: () => fetchPageBodies(page),
    // When the injection matches, render instantly and skip the network until
    // the data goes stale. When it doesn't (client-side nav / homepage), the
    // query fetches the page's bodies on mount.
    ...(seeded !== undefined ? { initialData: seeded } : {}),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const candidate = data?.[key];
  if (candidate !== undefined) {
    return candidate as SlotBodyByKind[K];
  }
  return def.default as SlotBodyByKind[K];
}

/**
 * Multi-slot variant of {@link useCmsSlot}: one hook call for a whole page's
 * worth of slots, returning a resolver you can call as many times as you like.
 *
 * Use this when the number of slots a component reads depends on its props —
 * `useCmsSlot` inside a `.map()` would be a hook in a loop. The resolver has
 * identical semantics to `useCmsSlot`, including throwing on an unknown key and
 * on a key belonging to another page (which would silently resolve to its
 * default forever, since only `page`'s bodies are in hand).
 */
export function useCmsSlots(
  page: PageId,
): <K extends SlotKind>(key: string) => SlotBodyByKind[K] {
  const seeded = seededBodiesFor(page);

  const { data } = useQuery({
    queryKey: ['cms', page],
    queryFn: () => fetchPageBodies(page),
    ...(seeded !== undefined ? { initialData: seeded } : {}),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  return <K extends SlotKind>(key: string): SlotBodyByKind[K] => {
    const def = getSlotDef(key);
    if (def === undefined) {
      throw new Error(
        `useCmsSlots: unknown key '${key}' — add it to packages/shared/src/cms/slots.ts`,
      );
    }
    if (def.page !== page) {
      throw new Error(`useCmsSlots('${page}'): key '${key}' belongs to page '${def.page}'.`);
    }
    const candidate = data?.[key];
    if (candidate !== undefined) return candidate as SlotBodyByKind[K];
    return def.default as SlotBodyByKind[K];
  };
}

/**
 * Multi-slot variant of {@link useCmsSlots} that also reports readiness, for a
 * component that resolves a variable number of IMAGE slots.
 *
 * `useCmsImage` gives one image plus `ready`, and `useCmsSlots` gives many
 * bodies without it — neither fits a page that renders one screenshot per
 * CMS-driven step, because the step count is data and the hook count can't be.
 * Image consumers gate on `ready` for the usual reason: so an admin-swapped
 * photo never paints the registry default first and then swaps.
 */
export function useCmsPage(page: PageId): {
  get: <K extends SlotKind>(key: string) => SlotBodyByKind[K];
  ready: boolean;
} {
  const seeded = seededBodiesFor(page);

  const { data, isFetched } = useQuery({
    queryKey: ['cms', page],
    queryFn: () => fetchPageBodies(page),
    ...(seeded !== undefined ? { initialData: seeded } : {}),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const get = <K extends SlotKind>(key: string): SlotBodyByKind[K] => {
    const def = getSlotDef(key);
    if (def === undefined) {
      throw new Error(`useCmsPage: unknown key '${key}' — add it to packages/shared/src/cms/slots.ts`);
    }
    if (def.page !== page) {
      throw new Error(`useCmsPage('${page}'): key '${key}' belongs to page '${def.page}'.`);
    }
    const candidate = data?.[key];
    if (candidate !== undefined) return candidate as SlotBodyByKind[K];
    return def.default as SlotBodyByKind[K];
  };

  return { get, ready: seeded !== undefined || isFetched };
}

/**
 * Image-slot variant of {@link useCmsSlot} that also reports whether the value
 * is authoritative yet. `ready` is true when the body came from a page-matched
 * injected blob (hard load) or once the client fetch has resolved (client-side
 * navigation). Image consumers gate rendering on `ready` so the photo never
 * paints the registry default first and then swaps to an admin override — on a
 * client-side navigation the injected blob is for the page the browser actually
 * loaded, so the destination page would otherwise flash its default image.
 */
export function useCmsImage(key: string): { image: ImageBody; ready: boolean } {
  const def = getSlotDef(key);
  if (def === undefined) {
    throw new Error(`useCmsImage: unknown key '${key}' — add it to packages/shared/src/cms/slots.ts`);
  }
  if (def.kind !== 'image') {
    throw new Error(`useCmsImage: slot '${key}' is kind '${def.kind}', not 'image'.`);
  }
  const page = def.page;

  const seeded = seededBodiesFor(page);

  const { data, isFetched } = useQuery({
    queryKey: ['cms', page],
    queryFn: () => fetchPageBodies(page),
    ...(seeded !== undefined ? { initialData: seeded } : {}),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // Authoritative when the injection covered this page, or the fetch completed.
  const ready = seeded !== undefined || isFetched;
  const candidate = data?.[key];
  const image = (candidate !== undefined ? candidate : def.default) as ImageBody;
  return { image, ready };
}

/**
 * Gallery variant of {@link useCmsImage} for an `image_list` slot. Returns the
 * ordered list of images plus `ready` (true once the value is authoritative —
 * a page-matched injected blob on hard load, or the client fetch resolved on a
 * cross-page navigation). Consumers gate rendering on `ready` for the same
 * reason single images do: so a destination page never paints the registry
 * default gallery first and then swaps to an admin-curated one.
 */
export function useCmsImageList(key: string): { images: ImageListBody; ready: boolean } {
  const def = getSlotDef(key);
  if (def === undefined) {
    throw new Error(`useCmsImageList: unknown key '${key}' — add it to packages/shared/src/cms/slots.ts`);
  }
  if (def.kind !== 'image_list') {
    throw new Error(`useCmsImageList: slot '${key}' is kind '${def.kind}', not 'image_list'.`);
  }
  const page = def.page;

  const seeded = seededBodiesFor(page);

  const { data, isFetched } = useQuery({
    queryKey: ['cms', page],
    queryFn: () => fetchPageBodies(page),
    ...(seeded !== undefined ? { initialData: seeded } : {}),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const ready = seeded !== undefined || isFetched;
  const candidate = data?.[key];
  const images = (candidate !== undefined ? candidate : def.default) as ImageListBody;
  return { images, ready };
}

/**
 * Warm the CMS query cache for every marketing page, so a client-side
 * navigation finds its bodies already cached rather than painting the registry
 * defaults for one round-trip.
 *
 * On any marketing page the worker now injects the whole set at first paint, so
 * this is a no-op there — the cache is already warm before React mounts, which
 * is the only way to beat a fast click. It still matters when the SPA was
 * entered somewhere the worker doesn't inject (an app route like /dashboard, or
 * HTML cached from before the whole-set rollout): one `page=all` request seeds
 * every page at once, rather than the twelve parallel per-page fetches this
 * used to fire.
 *
 * Fire once, after the initial page is interactive (called from main.tsx on
 * requestIdleCallback). Best-effort: a failure is swallowed and each page simply
 * fetches on first visit as before.
 */
export function prefetchMarketingCms(queryClient: QueryClient): void {
  if (readInjected()?.pages !== undefined) return;

  void (async () => {
    let pages: Record<string, PageBodies>;
    try {
      const res = await fetch('/api/__public__/cms?page=all', {
        headers: { accept: 'application/json' },
      });
      if (!res.ok) return;
      pages = ((await res.json()) as { pages?: Record<string, PageBodies> }).pages ?? {};
    } catch {
      return;
    }
    for (const page of PAGE_IDS) {
      // Don't clobber the page the injection already spoke for.
      if (queryClient.getQueryData(['cms', page]) !== undefined) continue;
      queryClient.setQueryData(['cms', page], pages[page] ?? {});
    }
  })();
}
